import { PharmacyOrdersStatusView } from "@/Common/Components/PharmacyOrders/PharmacyOrdersStatusView";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { LuxonDateHelper } from "@/Common/Helpers/LuxonDateHelper";
import { PrescriptionMedicationStatus } from "@/Common/Models/Patient/PatientEnums";
import { PharmacyOrderDistributionType, PharmacyOrderMedicationStatus } from "@/Common/Models/UI/Enums";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { Icon } from "@/DesignSystem";
import { PharmacyOrderHelper } from "../Helper/PharmacyOrderHelper";
import { PharmacyOrderModel } from "../Models/PharmacyOrderModel";

import styles from "./PharmacyOrderBasicInfo.module.scss";

interface IPharmacyOrderBasicInfoProps {
    pharmacyOrderInfo: PharmacyOrderModel | null;
}

export function PharmacyOrderBasicInfo(props: Readonly<IPharmacyOrderBasicInfoProps>): JSX.Element {
    const emptyValue = "--";
    const { getText } = useTranslation();

    function formatPreferredTimeOfDay(): JSX.Element {
        const { iconType, label } = PharmacyOrderHelper.getPreferredTimeOfDay(props?.pharmacyOrderInfo?.preferredTimeOfDay);

        if (iconType && label)
            return (
                <>
                    <span className={styles.icon} data-cy="pharmacy-order-basic-info-preferred-time-of-day">
                        <Icon size="sm">
                            {iconType}
                        </Icon>
                    </span>
                    {label}
                </>
            );

        return <></>;
    }

    function getPreferredDate(): JSX.Element {
        if (props?.pharmacyOrderInfo.preferredDate)
            return (
                <span data-cy="pharmacy-order-basic-info-preferred-date">
                    {LuxonDateTimeParser.parse(props?.pharmacyOrderInfo?.preferredDate)?.toFormat("yyyy-LL-dd")}
                </span>
            );

        return <></>;
    }

    function getMedicationCount(): JSX.Element {
        const orderMedications = props?.pharmacyOrderInfo?.medications ?? [];
        const allMedications = orderMedications.filter(medication => medication.medicationStatus !== parseInt(PrescriptionMedicationStatus.Interrupted))?.length;
        const approvedMedicationsCount = orderMedications?.filter(medications => medications.status == PharmacyOrderMedicationStatus.Approved).length;

        return (
            <span data-cy="pharmacy-order-basic-info-medication-count">
                {approvedMedicationsCount ?? emptyValue}/{allMedications}
            </span>
        );
    }

    if (!props.pharmacyOrderInfo) {
        return <div className={styles.orderInfo} />;
    }

    return (
        <div className={styles.orderInfo}>
            <div className={styles.orderBasicInfo}>
                <span className={styles.orderBasicInfoLable} data-cy="pharmacy-order-basic-info-order-status-label">
                    {getText("PharmacyOrders_OrderStatusLabel")}:
                </span>
                <span className={styles.icon}>
                    <PharmacyOrdersStatusView
                        orderStatus={props?.pharmacyOrderInfo?.orderStatus}
                    />
                </span>
                <span data-cy="pharmacy-order-basic-info-last-updated-date">
                    {props?.pharmacyOrderInfo?.lastUpdatedDate != null ? LuxonDateHelper.formatToFullDateTime(props?.pharmacyOrderInfo?.lastUpdatedDate) : emptyValue}
                </span>
                <span className={styles.orderBasicInfoLable} data-cy="pharmacy-order-basic-info-distribution-label">
                    {getText("PharmacyOrders_DistributionLabel")}:
                </span>
                {getMedicationCount()}
                <span data-cy="pharmacy-order-basic-info-distribution">
                    {props.pharmacyOrderInfo.distribution != null ? EnumHelper.getVocab(PharmacyOrderDistributionType, "Enums_PharmacyOrderDistribution_", props.pharmacyOrderInfo.distribution) : emptyValue}
                </span>
                {getPreferredDate()}
                {formatPreferredTimeOfDay()}
                <span className={styles.orderBasicInfoLable} data-cy="pharmacy-order-basic-info-filed-date-label">
                    {getText("PharmacyOrders_FiledLabel")}
                </span>
                <span>
                    {props.pharmacyOrderInfo.filedDate ? LuxonDateHelper.formatToFullDateTime(props.pharmacyOrderInfo.filedDate) : emptyValue}
                </span>
            </div>
            <div className={styles.orderId}>
                <span className={styles.orderBasicInfoLable} data-cy="pharmacy-order-basic-info-order-id-label">
                    {getText("PharmacyOrders_OrderIdLabel")}:
                </span>
                <span>
                    {props?.pharmacyOrderInfo?.orderId}
                </span>
            </div>
        </div>
    );
}
